'use client';

import Image from 'next/image';
import { useT } from '@/components/i18n-provider';
import { ChevronDown } from 'lucide-react';
import { useFullscreenNav } from './fullscreen-nav';
import { cn } from '@/lib/utils';

/**
 * Anon-mode replacement for `BrandCard`. Renders the same chrome
 * shape as the authed brand pill (Robutler mark + wordmark inside a
 * liquid-glass pill) but, instead of the authed workspace/account
 * menu, it's the anon visitor's "menu" entry point: clicking it opens
 * the login dialog. The onboarding overlay points its "menu" hint
 * here (`data-onboarding-anchor="menu"`, same key as the authed
 * brand card) so the splash reads identically for signed-out users.
 *
 * Lives next to `BrandCard` instead of replacing it because the
 * authed brand card carries a lot of state (workspace list, rename
 * flow, sub-menus) that we don't want to recompute conditionally for
 * every render. `WorkspaceShell` swaps between them based on
 * `currentUser === null`.
 */
export function AnonBrandCard({ className }: { className?: string }) {
  const t = useT();
  const { openHref } = useFullscreenNav();
  return (
    // `w-fit` so the wrapper shrink-wraps the pill rather than stretching
    // to the full chrome slot — keeps the onboarding "menu" arrow centred
    // on the visible button, not the wider block div.
    <div className={cn('relative pointer-events-auto w-fit', className)}>
      <button
        type="button"
        // Same key as the authed brand card so the onboarding overlay's
        // "menu" arrow lands here for anon visitors too.
        data-onboarding-anchor="menu"
        onClick={() => openHref('/login')}
        // Pinned size + shape to match the authed `BrandCard` trigger
        // so a side-by-side comparison reads as the same chrome element.
        className="h-10 rounded-2xl px-3 inline-flex items-center gap-2 select-none liquid-glass transition-colors hover:bg-accent/40"
        aria-label={t('anonCard.menuSignIn')}
      >
        <Image
          src="/icons/Logo_B.svg"
          alt="Robutler"
          width={22}
          height={22}
          className="block dark:hidden"
        />
        <Image
          src="/icons/Logo_W.svg"
          alt="Robutler"
          width={22}
          height={22}
          className="hidden dark:block"
        />
        <Image
          src="/icons/Wm_B.svg"
          alt="Robutler"
          width={92}
          height={13}
          className="hidden sm:block dark:hidden"
        />
        <Image
          src="/icons/Wm_W.svg"
          alt="Robutler"
          width={92}
          height={13}
          className="hidden dark:sm:block"
        />
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
}
